import { Box, Button, Divider, Flex, Group, Input, Select, Text } from '@byecode/ui'
import type { ArgumentsSchema, ArgumentsType } from '@lighthouse/core'
import type { FlowNode, NodeTypes, RunCodeActionConfig, RunCodeParam, RunCodeResult } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getInputInnerTypeMatch, getUpstreamNodesOptions, useAtomData, VariableSelect } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { innerTypeOptions } from '@/containers/FlowDetail/constants'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import CodeEditor from './CodeEditor'
import * as SC from './styles'

type CodeExecuteActionConfigureProps = {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: RunCodeActionConfig
    onChange: (value: RunCodeActionConfig) => void
}
export const CodeExecuteActionConfigure: React.FC<CodeExecuteActionConfigureProps> = ({ allParentNodes, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : undefined,
        [allParentNodes, dataSourceList, isInFlow, workflowList]
    )
    const userOption = useGetUserRecordOptions()

    return (
        <SC.Container>
            {(() => {
                const list: RunCodeActionConfig['inputVariables'] = value.inputVariables || []
                return (
                    <Group
                        label="输入变量"
                        extra={[
                            <Button
                                type="text"
                                size="sm"
                                key={1}
                                style={{ padding: '0 4px' }}
                                onClick={e => {
                                    const item: RunCodeParam = {
                                        id: nanoid(),
                                        name: '未命名变量',
                                        isEdit: false,
                                        value: undefined
                                    }
                                    onChange({ ...value, inputVariables: [...list, item] })
                                    e.stopPropagation()
                                }}
                            >
                                <SC.Icon type="Add2" size={20} />
                            </Button>
                        ]}
                    >
                        <Flex pt={8} gap={12} direction="column" pb={4} px={8}>
                            {list.map((item, index) => (
                                <Flex key={item.id} gap={8} direction="column">
                                    <Flex alignItems="center" gap={6}>
                                        <Flex alignItems="center" gap={6} style={{ flex: 1, height: 28, overflow: 'hidden' }}>
                                            {item.isEdit ? (
                                                <Input
                                                    autoSelect
                                                    size="sm"
                                                    placeholder="变量名"
                                                    defaultValue={item.name}
                                                    onBlur={e => {
                                                        const nextName = e.target.value
                                                        const nextList = [...list]
                                                        nextList.splice(index, 1, {
                                                            ...item,
                                                            name: nextName || '未命名变量',
                                                            isEdit: false
                                                        })
                                                        onChange({ ...value, inputVariables: nextList })
                                                    }}
                                                />
                                            ) : (
                                                <>
                                                    <Text size={14}>{item.name}</Text>
                                                    {!item.isEdit && (
                                                        <SC.Icon
                                                            type="edit"
                                                            size={12}
                                                            color="var(--color-gray-400)"
                                                            onClick={() => {
                                                                const nextList = [...list]
                                                                nextList.splice(index, 1, { ...item, isEdit: true })
                                                                onChange({ ...value, inputVariables: nextList })
                                                            }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Flex>
                                        <SC.Icon
                                            type="Trash"
                                            size={16}
                                            color="var(--color-gray-400)"
                                            onClick={() => {
                                                const nextList = [...list]
                                                nextList.splice(index, 1)
                                                onChange({ ...value, inputVariables: nextList })
                                            }}
                                        />
                                    </Flex>
                                    <VariableSelect
                                        withinPortal
                                        innerTypes={getInputInnerTypeMatch('TEXT')}
                                        upstreamOptions={upstreamOptions}
                                        disablePexels
                                        value={item.value || undefined}
                                        width="100%"
                                        userOption={userOption}
                                        onChange={newValue => {
                                            const nextList = [...list]
                                            nextList.splice(index, 1, { ...item, value: newValue })
                                            onChange({ ...value, inputVariables: nextList })
                                        }}
                                    />
                                </Flex>
                            ))}
                        </Flex>
                    </Group>
                )
            })()}

            <Divider />
            <Group label="代码">
                <SC.CodeEditor>
                    <CodeEditor code={value.code} onChange={code => onChange({ ...value, code })} />
                </SC.CodeEditor>
            </Group>

            <Divider />

            {(() => {
                const list = value.args || []

                const handleAddItem = (e: React.MouseEvent) => {
                    const item: ArgumentsSchema = { id: nanoid(), name: '', innerType: 'TEXT' }
                    onChange({ ...value, args: [...list, item] })
                    e.stopPropagation()
                }

                const handleInputChange = (index: number, newName: string) => {
                    const nextList = [...list]
                    nextList.splice(index, 1, { ...list[index], name: newName })
                    onChange({ ...value, args: nextList })
                }

                const handleSelectChange = (index: number, newValue: RunCodeResult['innerType']) => {
                    const nextList = [...list]
                    nextList.splice(index, 1, { ...list[index], innerType: newValue as ArgumentsType })
                    onChange({ ...value, args: nextList })
                }

                const handleDeleteItem = (index: number) => {
                    const nextList = [...list]
                    nextList.splice(index, 1)
                    onChange({ ...value, args: nextList })
                }

                return (
                    <Group
                        label="输出变量"
                        extra={[<SC.Icon style={{ marginLeft: 8 }} onClick={handleAddItem} key={1} type="Add2" size={16} />]}
                    >
                        <Box px={8}>
                            {list.map((item, index) => (
                                <SC.VariableItem key={item.id}>
                                    <SC.InputWrapper>
                                        <Input
                                            placeholder="请输入"
                                            value={item.name}
                                            onChange={e => handleInputChange(index, e.target.value)}
                                        />
                                    </SC.InputWrapper>
                                    <SC.SelectWrapper>
                                        <Select
                                            placeholder="请选择"
                                            options={innerTypeOptions}
                                            value={item.innerType}
                                            onChange={v => handleSelectChange(index, v as RunCodeResult['innerType'])}
                                        />
                                    </SC.SelectWrapper>
                                    <SC.DeleteWrapper>
                                        <SC.Icon type="Trash" size={16} onClick={() => handleDeleteItem(index)} />
                                    </SC.DeleteWrapper>
                                </SC.VariableItem>
                            ))}
                        </Box>
                    </Group>
                )
            })()}
        </SC.Container>
    )
}
